import { Map as ImmutableMap, Record, type RecordOf } from 'immutable';

import type { QcsSyncResourceStore, QcsSyncResourceStoreProps } from 'app-components/flux/core/resourceStoreTypes';

export const FUNDING_ACCOUNTS_REDUCER_KEY = 'fundingAccountStore';

export const FundingAccountParentTypeEnum = Object.freeze({
  BANK: 'BANK',
  INVESTMENT: 'INVESTMENT',
});

export type FundingAccountType =
  | 'CHECKING'
  | 'SAVINGS'
  | 'MONEY_MARKET'
  | 'CASH_MANAGEMENT'
  | 'BROKERAGE'
  | 'IRA'
  | 'KEOGH'
  | 'ROTH_IRA'
  | 'SEP_IRA'
  | 'SIMPLE_IRA'
  | '529'
  | 'UGMA_UTMA'
  | 'OTHER_INVESTMENTS'
  | 'UNKNOWN';
export const FundingAccountTypeEnum = Object.freeze({
  CHECKING: 'CHECKING',
  SAVINGS: 'SAVINGS',
  MONEY_MARKET: 'MONEY_MARKET',
  CASH_MANAGEMENT: 'CASH_MANAGEMENT',
  BROKERAGE: 'BROKERAGE',
  _401K: '401K',
  IRA: 'IRA',
  KEOGH: 'KEOGH',
  ROTH_IRA: 'ROTH_IRA',
  ROTH_401K: 'ROTH_401K',
  SEP_IRA: 'SEP_IRA',
  SIMPLE_IRA: 'SIMPLE_IRA',
  _529: '529',
  UGMA_UTMA: 'UGMA_UTMA',
  _403B: '403B',
  OTHER_INVESTMENTS: 'OTHER_INVESTMENTS',
});

export type AccountOwnerType = 'PERSONAL' | 'JOINT' | 'BUSINESS';
export const FundingAccountOwnerTypeEnum = Object.freeze({
  PERSONAL: 'PERSONAL',
  JOINT: 'JOINT',
  BUSINESS: 'BUSINESS',
});

export type VerificationStatus =
  | 'MICRO_DEPOSIT_VERIFICATION_REQUIRED'
  | 'MICRO_DEPOSIT_VERIFICATION_CONFIRMED'
  | 'MICRO_DEPOSIT_VERIFICATION_COMPLETED'
  | 'MICRO_DEPOSIT_VERIFICATION_FAILED'
  | 'MICRO_DEPOSIT_INITIATED'
  | 'MICRO_DEPOSIT_POSTED'
  | 'MICRO_DEPOSIT_FAILED'
  | 'VERIFICATION_NOT_NEEDED'
  | 'VERIFICATION_NOT_SUPPORTED'
  | 'MICRO_DEPOSIT_VERIFICATION_AMOUNTS_INVALID';
export const verificationStatusEnum = Object.freeze({
  MICRO_DEPOSIT_VERIFICATION_REQUIRED: 'MICRO_DEPOSIT_VERIFICATION_REQUIRED',
  MICRO_DEPOSIT_VERIFICATION_CONFIRMED: 'MICRO_DEPOSIT_VERIFICATION_CONFIRMED',
  MICRO_DEPOSIT_VERIFICATION_COMPLETED: 'MICRO_DEPOSIT_VERIFICATION_COMPLETED',
  MICRO_DEPOSIT_VERIFICATION_FAILED: 'MICRO_DEPOSIT_VERIFICATION_FAILED',
  MICRO_DEPOSIT_VERIFICATION_AMOUNTS_INVALID: 'MICRO_DEPOSIT_VERIFICATION_AMOUNTS_INVALID',

  MICRO_DEPOSIT_INITIATED: 'MICRO_DEPOSIT_INITIATED',
  MICRO_DEPOSIT_POSTED: 'MICRO_DEPOSIT_POSTED',
  MICRO_DEPOSIT_FAILED: 'MICRO_DEPOSIT_FAILED', // bank could not post amounts
  VERIFICATION_NOT_NEEDED: 'VERIFICATION_NOT_NEEDED',
  VERIFICATION_NOT_SUPPORTED: 'VERIFICATION_NOT_SUPPORTED',
});

// a bit silly, but QCS has returned accounts grouped into these buckets:
export type EligigibilityGroup = 'eligible' | 'pendingVerification' | 'available' | 'inactive';
export const EligigibilityGroupEnum = Object.freeze({
  eligible: 'eligible',
  pendingVerification: 'pendingVerification',
  available: 'available',
  inactive: 'inactive',
});

export type FundingAccountProps = {
  id?: string;
  routingNumber?: string;
  accountNumber?: string;
  nickname?: string;
  accountType?: FundingAccountType;
  ownerType?: AccountOwnerType;
  linkedAccountId?: string;
  verificationStatus?: VerificationStatus;
  verificationId?: string;
  eligibility?: EligigibilityGroup;
  isDeleted?: boolean;
  destinationOnly?: boolean;
  maxTransferAmount?: number;
};

export type FundingAccount = RecordOf<FundingAccountProps>;

export const mkFundingAccount = Record<FundingAccountProps>({
  id: undefined,
  routingNumber: undefined,
  accountNumber: undefined,
  nickname: undefined,
  accountType: undefined,
  ownerType: undefined,
  linkedAccountId: undefined,
  verificationStatus: undefined,
  verificationId: undefined,
  eligibility: undefined,
  isDeleted: undefined,
  destinationOnly: undefined,
  maxTransferAmount: undefined,
});

// store
type FundingAccountStoreProps = QcsSyncResourceStoreProps<FundingAccountProps>;
export type FundingAccountStore = QcsSyncResourceStore<FundingAccountProps, FundingAccountStoreProps>;
export const mkFundingAccountStore = Record<FundingAccountStoreProps>({
  resourcesById: ImmutableMap(),

  resourceName: 'fundingAccount',
  lastSyncDate: null,
  loadPending: true,
  isLoading: false,
  error: undefined,
});

// payloads
export type CreateFundingAccountPayload = {
  routingNumber: string;
  accountNumber: string;
  accountType: FundingAccountType;
  ownerType: AccountOwnerType;
  nickname: string;
  linkedAccountId: string;
};

export type VerifyMicrodepositsPayload = {
  id: string;
  amountOne: string;
  amountTwo: string;
};
